import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { AxiosError } from 'axios'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { Toast } from '@/common/components/Toast'
import { OrderBy, useOrderBy } from '@/common/hooks/useOrderBy'
import { personModel } from '@/common/models/person'
import { userModel } from '@/common/models/user'
import { userPreferenceModel } from '@/common/models/userPreference'
import { calculateItemsPerPage } from '@/common/utils/calculateItemsPerPage'
import { translateUserType } from '@/common/utils/translateUserType'
import { api, ApiError, APIPaginated, ApiWhere } from '@/lib/axios'
import { FilterUsersForm } from '../schemas/FilterUsersSchema'
import { useFilterUsers } from './useFilterUsers'

export const userPaginatedDTO = userModel.extend({
  UserPreference: userPreferenceModel,
  Person: personModel,
})

export type UserPaginated = z.infer<typeof userPaginatedDTO>

export type GetPaginateUsersProps = {
  page: number
  orderBy: OrderBy
  filters: FilterUsersForm
}

async function getPaginateUsers(props: GetPaginateUsersProps) {
  const { filters } = props

  const where: ApiWhere = {
    isActive: !filters.isActive,
    ...(!filters.isActive && { email: { not: { startsWith: 'deleting_' } } }),
    ...(filters.userType && { userType: filters.userType }),
    ...(filters.gender && {
      Person: { genre: filters.gender },
    }),
    ...(filters.birthMonth && {
      Person: { birthMonth: filters.birthMonth },
    }),
    ...(filters.birthYear && {
      Person: { birthYear: filters.birthYear },
    }),
    ...(filters.startCreated &&
      filters.endCreated && {
        createdAt: {
          gte: filters.startCreated,
          lte: filters.endCreated,
        },
      }),
    ...(filters.freeSearch && {
      AND: [
        {
          OR: [
            { email: { contains: filters.freeSearch } },
            { Person: { name: { contains: filters.freeSearch } } },
            { Person: { documentNumber: { contains: filters.freeSearch } } },
            {
              UserPreference: { displayName: { contains: filters.freeSearch } },
            },
          ],
        },
      ],
    }),
  }

  const { data } = await api.backend.get<APIPaginated<UserPaginated>>(
    '/user/paginate',
    {
      params: {
        page: props.page,
        limit: 25,
        relations: {
          orderBy: props.orderBy,
          where,
          include: {
            Person: true,
            UserPreference: true,
          },
        },
      },
    },
  )

  return data
}
export const usePaginateUsers = () => {
  const [tableSearch, setTableSearch] = useState('')
  const [tableSearchResult, setTableSearchResult] = useState<UserPaginated[]>(
    [],
  )
  const [page, setPage] = useState(1)
  const [filters] = useFilterUsers()

  const { t } = useTranslation(['common', 'registers'])
  const debouncedTableSearch = useDebounce(tableSearch, 300)
  const { orderBy, handleChangeOrder } = useOrderBy()
  const { data, isLoading, error } = useQuery<
    APIPaginated<UserPaginated>,
    AxiosError<ApiError>
  >({
    queryKey: ['/user/paginate', page, orderBy, ...Object.values(filters)],
    queryFn: () => getPaginateUsers({ page, orderBy, filters }),
  })

  const handleUsersSearch = (search: string) => {
    setTableSearch(search?.toLowerCase() || '')
  }

  const handleChangePage = (page: number) => {
    setPage(page)
  }

  const usersBadges = useMemo(() => {
    return {
      userType: new Set(
        data?.items?.map((register) => register.userType) || [],
      ).size.toFixed(0),
    }
  }, [data])

  useEffect(() => {
    if (!error) return

    toast(Toast, {
      data: {
        type: 'danger',
        icon: ServerCrash,
        title: t('registers:users.paginate_table.toast_error.title'),
        description:
          error?.response?.data?.friendlyMessage ||
          t('registers:users.paginate_table.toast_error.description'),
      },
    })
  }, [error, t])

  useEffect(() => {
    if (!data || data?.items?.length === 0) return

    const filteredData = data.items.filter(
      (user) =>
        user.Person.name.includes(debouncedTableSearch) ||
        user.email.includes(debouncedTableSearch) ||
        translateUserType(t, user.userType).includes(debouncedTableSearch),
    )

    setTableSearchResult(filteredData)
  }, [data, debouncedTableSearch, t])

  return {
    users: debouncedTableSearch ? tableSearchResult : data?.items || [],
    usersPaginated: data?.meta,
    isLoadingUsers: isLoading,
    usersOrderBy: orderBy,
    usersBadges,
    usersPerPage: calculateItemsPerPage(data?.meta),
    filtersQty: Object.values(filters).filter(Boolean).length,
    handleUsersSearch,
    handleChangePage,
    handleChangeOrder,
  }
}
